import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';

const ACKS = ['Thank you.', 'I see.', 'Noted.', 'Alright.', 'Understood.'];
const MAX_RECONNECTS = 3;

// Real-time D-ID avatar over WebRTC with auto-reconnect.
// Attach `videoRef` to a <video>; call connect(), speak(text), acknowledge(), disconnect().
export const useDidStream = () => {
  const videoRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const pcRef = useRef(null);
  const sessionRef = useRef(null);
  const activeClips = useRef(0);
  const speakingTimer = useRef(null);
  const intentionalClose = useRef(false);
  const reconnects = useRef(0);
  const connectingRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle | connecting | connected | reconnecting | failed
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [presenterImage, setPresenterImage] = useState(null);

  const post = (path, body) => api.post(path, body, { silent: true }).then(r => r.data);
  const del = (s) => s && api.delete(`/interview/stream/${s.stream_id}`, { data: { session_id: s.session_id }, silent: true }).catch(() => {});

  const attachStream = (el) => {
    if (!el || !remoteStreamRef.current) return;
    if (el.srcObject !== remoteStreamRef.current) {
      el.srcObject = remoteStreamRef.current;
      el.onloadeddata = () => setHasVideo(true);
    }
    el.muted = false;
    el.play().catch(() => {});
  };

  const videoRefCallback = useCallback((el) => {
    videoRef.current = el;
    attachStream(el);
  }, []);

  const closePeer = () => {
    if (speakingTimer.current) clearTimeout(speakingTimer.current);
    activeClips.current = 0;
    pcRef.current?.close();
    pcRef.current = null;
    remoteStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setHasVideo(false);
    setIsSpeaking(false);
  };

  const scheduleReconnect = () => {
    if (intentionalClose.current || reconnects.current >= MAX_RECONNECTS) { setStatus('failed'); return; }
    reconnects.current += 1;
    setStatus('reconnecting');
    const old = sessionRef.current;
    sessionRef.current = null;
    closePeer();
    del(old);
    setTimeout(() => { openStream().catch(() => {}); }, 800 * reconnects.current);
  };

  const openStream = async () => {
    if (connectingRef.current) return connectingRef.current;
    connectingRef.current = (async () => {
      const s = await post('/interview/stream/create');
      sessionRef.current = s;
      if (s.presenter_image) setPresenterImage(s.presenter_image);
      const pc = new RTCPeerConnection({ iceServers: s.ice_servers });
      pcRef.current = pc;

      pc.ontrack = (ev) => {
        if (ev.streams && ev.streams[0]) {
          remoteStreamRef.current = ev.streams[0];
        } else {
          if (!remoteStreamRef.current) remoteStreamRef.current = new MediaStream();
          remoteStreamRef.current.addTrack(ev.track);
        }
        attachStream(videoRef.current);
      };

      const pendingIce = [];
      let sdpAccepted = false;
      const sendIce = (c) => post(`/interview/stream/${s.stream_id}/ice`, { session_id: s.session_id, candidate: c }).catch(() => {});
      pc.onicecandidate = (ev) => {
        if (sessionRef.current !== s) return;
        const c = ev.candidate ? { candidate: ev.candidate.candidate, sdpMid: ev.candidate.sdpMid, sdpMLineIndex: ev.candidate.sdpMLineIndex } : null;
        if (sdpAccepted) sendIce(c); else pendingIce.push(c);
      };
      pc.ondatachannel = (ev) => {
        ev.channel.onmessage = (m) => {
          const msg = String(m.data || '');
          if (msg.startsWith('stream/started')) { activeClips.current += 1; setIsSpeaking(true); }
          if (msg.startsWith('stream/done') || msg.startsWith('stream/error')) {
            activeClips.current = Math.max(0, activeClips.current - 1);
            if (activeClips.current === 0) setIsSpeaking(false);
          }
        };
      };

      let connectTimer;
      const connected = new Promise((resolve, reject) => {
        connectTimer = setTimeout(() => reject(new Error('WebRTC connect timeout')), 25000);
        pc.onconnectionstatechange = () => {
          const st = pc.connectionState;
          if (st === 'connected') { clearTimeout(connectTimer); reconnects.current = 0; setStatus('connected'); resolve(true); }
          if (st === 'failed' || st === 'disconnected' || st === 'closed') {
            clearTimeout(connectTimer);
            reject(new Error(`WebRTC ${st}`));
            if (pcRef.current === pc && !intentionalClose.current) scheduleReconnect();
          }
        };
      });
      connected.catch(() => {});

      await pc.setRemoteDescription(s.offer);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await post(`/interview/stream/${s.stream_id}/sdp`, { session_id: s.session_id, answer: { type: answer.type, sdp: answer.sdp } });
      sdpAccepted = true;
      pendingIce.splice(0).forEach(sendIce);
      try { await connected; } finally { clearTimeout(connectTimer); }
      return true;
    })();
    try {
      return await connectingRef.current;
    } finally {
      connectingRef.current = null;
    }
  };

  const connect = useCallback(async () => {
    if (pcRef.current && pcRef.current.connectionState === 'connected') return true;
    intentionalClose.current = false;
    reconnects.current = 0;
    setStatus('connecting');
    try {
      return await openStream();
    } catch (e) {
      console.warn('D-ID stream unavailable:', e?.message || e);
      del(sessionRef.current);
      sessionRef.current = null;
      closePeer();
      setStatus('failed');
      return false;
    }
  }, []);

  const disconnect = useCallback(async () => {
    intentionalClose.current = true;
    const s = sessionRef.current;
    sessionRef.current = null;
    closePeer();
    setStatus('idle');
    await del(s);
  }, []);

  const waitForLive = async (ms = 12000) => {
    const end = Date.now() + ms;
    while (Date.now() < end) {
      if (pcRef.current?.connectionState === 'connected' && sessionRef.current) return true;
      if (!connectingRef.current && !pcRef.current) return false;
      await new Promise(r => setTimeout(r, 300));
    }
    return false;
  };

  // Returns true if the avatar will speak; false → caller should fall back to TTS.
  const speak = useCallback(async (text) => {
    if (!(pcRef.current?.connectionState === 'connected' && sessionRef.current)) {
      if (!(await waitForLive())) return false;
    }
    const s = sessionRef.current;
    try {
      const r = await post(`/interview/stream/${s.stream_id}/speak`, { session_id: s.session_id, text });
      setIsSpeaking(true);
      if (speakingTimer.current) clearTimeout(speakingTimer.current);
      speakingTimer.current = setTimeout(() => { if (activeClips.current === 0) setIsSpeaking(false); }, ((r.estimated_seconds || 5) + 4) * 1000);
      return true;
    } catch (e) {
      console.warn('D-ID speak failed:', e?.response?.data || e?.message);
      return false;
    }
  }, []);

  // Instant short acknowledgement while the next question is being generated (queued on the stream)
  const acknowledge = useCallback(() => {
    if (!(pcRef.current?.connectionState === 'connected' && sessionRef.current)) return false;
    const s = sessionRef.current;
    post(`/interview/stream/${s.stream_id}/speak`, { session_id: s.session_id, text: ACKS[Math.floor(Math.random() * ACKS.length)] }).catch(() => {});
    return true;
  }, []);

  useEffect(() => () => { intentionalClose.current = true; const s = sessionRef.current; sessionRef.current = null; closePeer(); del(s); }, []);

  return {
    videoRef: videoRefCallback, status, isSpeaking, presenterImage, connect, speak, acknowledge, disconnect,
    isLive: status === 'connected' && hasVideo,
  };
};
